import type { DiscoveryMode, DiscoverySessionContext } from "@/domain/types";

export const DISCOVERY_MODES = ["live", "demo", "simulator"] as const;
export const QOTTA_DISCOVERY_MESSAGE_VERSION = 1 as const;

export type DiscoveryLaunchOptions = {
  previewId?: string;
  adminView?: boolean;
  publicCustomerSlug: string;
  mode: DiscoveryMode;
  sessionId?: string | undefined;
  discoveryBaseUrl?: string | undefined;
};

export type DiscoverySource = Pick<
  DiscoverySessionContext,
  "sourceUrl" | "sourcePath" | "sourceTitle"
>;

export type DiscoveryRouteSearch = {
  previewId?: string;
  adminView?: boolean;
  mode?: DiscoveryMode | undefined;
  sourceUrl?: string | undefined;
  sourcePath?: string | undefined;
  sourceTitle?: string | undefined;
  sessionId?: string | undefined;
  parentOrigin?: string | undefined;
};

export type QottaDiscoveryMessage = {
  type: "qotta:close" | "qotta:signup";
  version: typeof QOTTA_DISCOVERY_MESSAGE_VERSION;
} | {
  type: "qotta:submitting";
  version: typeof QOTTA_DISCOVERY_MESSAGE_VERSION;
  pending: boolean;
};

type LocationLike = Pick<Location, "href" | "origin" | "pathname" | "search">;

export function isDiscoveryMode(value: unknown): value is DiscoveryMode {
  return typeof value === "string" && DISCOVERY_MODES.includes(value as DiscoveryMode);
}

export function parseDiscoveryRouteSearch(search: Record<string, unknown>): DiscoveryRouteSearch {
  return {
    ...(typeof search["previewId"] === "string" && isOpaqueSessionId(search["previewId"]) ? { previewId: search["previewId"] } : {}),
    ...(search["adminView"] === true || search["adminView"] === "true" ? { adminView: true } : {}),
    ...(isDiscoveryMode(search["mode"]) ? { mode: search["mode"] } : {}),
    ...(typeof search["sourceUrl"] === "string" ? { sourceUrl: search["sourceUrl"] } : {}),
    ...(typeof search["sourcePath"] === "string" ? { sourcePath: search["sourcePath"] } : {}),
    ...(typeof search["sourceTitle"] === "string" ? { sourceTitle: search["sourceTitle"] } : {}),
    ...(typeof search["sessionId"] === "string" && isOpaqueSessionId(search["sessionId"])
      ? { sessionId: search["sessionId"] }
      : {}),
    ...(normalizeOrigin(search["parentOrigin"])
      ? { parentOrigin: normalizeOrigin(search["parentOrigin"])! }
      : {}),
  };
}

function isOpaqueSessionId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function captureDiscoverySource(
  location: LocationLike = window.location,
  title = document.title,
): DiscoverySource {
  return {
    sourceUrl: location.href,
    sourcePath: `${location.pathname}${location.search}`,
    sourceTitle: title,
  };
}

export function buildDiscoveryIframeUrl(
  options: DiscoveryLaunchOptions,
  source: DiscoverySource,
  hostLocation: Pick<Location, "href" | "origin"> = window.location,
): string {
  const discoveryOrigin = new URL(
    options.discoveryBaseUrl ?? hostLocation.origin,
    hostLocation.href,
  ).origin;
  const url = new URL(
    `/discovery/${encodeURIComponent(options.publicCustomerSlug)}`,
    discoveryOrigin,
  );

  url.searchParams.set("mode", options.mode);
  if (options.previewId) url.searchParams.set("previewId", options.previewId);
  if (options.adminView) url.searchParams.set("adminView", "true");
  url.searchParams.set("sourceUrl", source.sourceUrl);
  url.searchParams.set("sourcePath", source.sourcePath);
  url.searchParams.set("sourceTitle", source.sourceTitle);
  url.searchParams.set("parentOrigin", hostLocation.origin);
  if (options.sessionId) url.searchParams.set("sessionId", options.sessionId);

  return url.origin === hostLocation.origin ? `${url.pathname}${url.search}` : url.toString();
}

export function resolveDiscoveryRuntimeContext(
  search: DiscoveryRouteSearch,
  fallback: { href: string; referrer: string; title: string },
): DiscoverySessionContext & { parentOrigin: string | null } {
  const sourceUrl = search.sourceUrl || fallback.referrer || fallback.href;
  let sourcePath = search.sourcePath ?? "";
  if (!sourcePath) {
    try {
      const source = new URL(sourceUrl);
      sourcePath = `${source.pathname}${source.search}`;
    } catch {
      sourcePath = "";
    }
  }

  return {
    mode: search.mode ?? "live",
    sourceUrl,
    sourcePath,
    sourceTitle: search.sourceTitle ?? fallback.title,
    sessionId: search.sessionId ?? "",
    parentOrigin: normalizeOrigin(search.parentOrigin) || normalizeOriginFromUrl(fallback.referrer),
  };
}

export function closeDiscoveryMessage(): QottaDiscoveryMessage {
  return { type: "qotta:close", version: QOTTA_DISCOVERY_MESSAGE_VERSION };
}

export function signupDiscoveryMessage(): QottaDiscoveryMessage {
  return { type: "qotta:signup", version: QOTTA_DISCOVERY_MESSAGE_VERSION };
}

export function isQottaDiscoveryMessage(value: unknown): value is QottaDiscoveryMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<QottaDiscoveryMessage>;
  return ((message.type === "qotta:close" || message.type === "qotta:signup") &&
    message.version === QOTTA_DISCOVERY_MESSAGE_VERSION) ||
    (message.type === "qotta:submitting" && typeof message.pending === "boolean" &&
      message.version === QOTTA_DISCOVERY_MESSAGE_VERSION);
}

export function postDiscoveryMessage(
  target: Window,
  message: QottaDiscoveryMessage,
  allowedOrigin: string,
) {
  const targetOrigin = normalizeOrigin(allowedOrigin);
  if (!targetOrigin) return false;
  target.postMessage(message, targetOrigin);
  return true;
}

export function normalizeOrigin(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url.origin : null;
  } catch {
    return null;
  }
}

function normalizeOriginFromUrl(value: string): string | null {
  return normalizeOrigin(value);
}
